import { useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  YouTube Iframe API helpers                                        */
/* ------------------------------------------------------------------ */

/** Subconjunto de la YouTube IFrame Player API que este hook realmente usa. */
export interface YTPlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
}

export interface YTPlayerConfig {
  events?: {
    onReady?: () => void;
    onStateChange?: (event: { data: number }) => void;
  };
}

export type YTPlayerConstructor = new (elementId: string, config: YTPlayerConfig) => YTPlayer;

export interface YTNamespace {
  Player: YTPlayerConstructor;
}

export type YTWindow = Window & {
  YT?: YTNamespace;
  onYouTubeIframeAPIReady?: () => void;
};

/** Load the YouTube Iframe API script once (idempotent). */
export function ensureYTApi(): Promise<void> {
  const w = window as YTWindow;
  if (w.YT?.Player) return Promise.resolve();

  return new Promise((resolve) => {
    // If the script tag already exists, just wait for the callback
    if (document.getElementById('yt-iframe-api')) {
      const prev = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => {
        prev?.();
        resolve();
      };
      return;
    }

    w.onYouTubeIframeAPIReady = () => resolve();
    const tag = document.createElement('script');
    tag.id = 'yt-iframe-api';
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
}

/**
 * Hook that tracks YouTube video watch progress via the Iframe API.
 * Returns high-water-mark percentage (0-100).
 *
 * Polling starts as soon as the player is READY — not only on the PLAYING state
 * event. Binding a YT.Player to a pre-existing iframe can miss the PLAYING
 * transition, which previously left progress stuck at 0% until the video ENDED
 * (Bug #3: "only 100% let me complete"). Polling reads getCurrentTime directly,
 * so a missed state event no longer breaks the threshold gate.
 */
export function useYouTubeProgress(
  iframeId: string,
  enabled: boolean,
  onProgress?: (pct: number) => void,
): number {
  const playerRef = useRef<YTPlayer | null>(null);
  const highWaterRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);
  const [pct, setPct] = useState(0);
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const report = (value: number) => {
      if (value > highWaterRef.current) {
        highWaterRef.current = value;
        setPct(value);
        onProgressRef.current?.(value);
      }
    };

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        const p = playerRef.current;
        if (!p?.getCurrentTime || !p?.getDuration) return;
        const dur = p.getDuration();
        if (!dur || !Number.isFinite(dur)) return;
        report(Math.round((p.getCurrentTime() / dur) * 100));
      }, 1000);
    };

    ensureYTApi().then(() => {
      if (cancelled) return;
      const w = window as YTWindow;

      // Poll until the iframe element exists in DOM (may render slightly after)
      const waitForIframe = setInterval(() => {
        const el = document.getElementById(iframeId);
        if (!el || cancelled) {
          if (cancelled) clearInterval(waitForIframe);
          return;
        }
        clearInterval(waitForIframe);

        const YT = w.YT;
        if (!YT) return; // ensureYTApi ya garantizó YT.Player; guarda de tipo.
        playerRef.current = new YT.Player(iframeId, {
          events: {
            // Start polling the moment the player is usable — robust against a
            // missed PLAYING state event.
            onReady: () => startPolling(),
            onStateChange: (e: { data: number }) => {
              // YT.PlayerState.PLAYING === 1, ENDED === 0
              if (e.data === 1) {
                startPolling();
              } else if (e.data === 0) {
                // Video finished → guarantee 100%.
                report(100);
              }
            },
          },
        });
      }, 200);

      // Cleanup wait interval on cancel
      return () => clearInterval(waitForIframe);
    });

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Don't call destroy() — React owns the iframe DOM node.
      // Just release the reference so it can be GC'd.
      playerRef.current = null;
    };
  }, [enabled, iframeId]);

  return pct;
}
